import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Mail, Send, MessageCircle, Phone, AlertCircle, Loader2 } from 'lucide-react';
import { organizationInfo } from '../mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    contactType: 'general'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, formData);
      toast.success(res.data.message || "Thank you for reaching out! We'll be in touch soon.");
      setFormData({ name: '', email: '', subject: '', message: '', contactType: 'general' });
    } catch (err) {
      toast.error('Something went wrong sending your message. Please email info@sagedrecovery.org.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="py-16 bg-gray-50">
        <div className="container text-center">
          <h1 className="heading-1 mb-6">Let's Connect</h1>
          <p className="body-large max-w-3xl mx-auto">
            We're here to help. Whether you have a question, are looking
            for resources, or want to get involved, we'd love to hear from you. Send a message below or
            email us — we respond personally.
          </p>
        </div>
      </section>

      {/* Crisis Support Alert */}
      <section className="py-8 bg-red-50 border-l-4 border-red-500">
        <div className="container">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-red-800 mb-2">In Crisis? Get Help Now</h3>
              <p className="text-red-700 mb-3">
                If you or someone you know is in immediate danger or experiencing thoughts of self-harm,
                please reach out right away. You are not alone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:911"
                  className="inline-flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-full font-medium hover:bg-gray-900 transition-colors"
                >
                  Emergency: 911
                </a>
                <a
                  href="tel:988"
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full font-medium hover:bg-red-700 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Call or Text 988
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-16">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="heading-2 mb-8">Get In Touch</h2>

              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="p-3 rounded-full bg-green-100">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1">Email Us</h3>
                    <a
                      href={`mailto:${organizationInfo.email}`}
                      className="body-medium font-medium text-green-600 hover:text-green-700 transition-colors"
                    >
                      {organizationInfo.email}
                    </a>
                    <p className="body-small mt-1">We read and respond to every message personally.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-3 rounded-full bg-green-100">
                    <MapPin className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1">Where We Serve</h3>
                    <p className="body-medium">{organizationInfo.address}</p>
                    <p className="body-small mt-1">Virtual support across Manistee and Mason Counties, Michigan.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-3 rounded-full bg-green-100">
                    <MessageCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1">Chat With Sage</h3>
                    <p className="body-medium">Have a quick question?</p>
                    <p className="body-small mt-1">
                      Use the chat bubble in the corner to talk with our virtual assistant any time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-green-50 rounded-lg">
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Looking for resources?</h4>
                <p className="body-small mb-4">
                  Browse trusted crisis, recovery, and local support resources available right now.
                </p>
                <Link to="/resources" className="btn-secondary inline-flex">View Resources</Link>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h2 className="heading-2 mb-6">Send Us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block body-medium font-medium mb-2">Full Name *</label>
                  <input
                    data-testid="contact-name"
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block body-medium font-medium mb-2">Email Address *</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label className="block body-medium font-medium mb-2">What's this about?</label>
                  <select
                    data-testid="contact-type"
                    name="contactType"
                    value={formData.contactType}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="general">General Question</option>
                    <option value="resources">Looking for Resources</option>
                    <option value="support">Support for Myself or a Loved One</option>
                    <option value="volunteer">Volunteer Interest</option>
                    <option value="partnership">Partnership Inquiry</option>
                  </select>
                </div>

                <div>
                  <label className="block body-medium font-medium mb-2">Subject</label>
                  <input
                    data-testid="contact-subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Brief description"
                  />
                </div>

                <div>
                  <label className="block body-medium font-medium mb-2">Message *</label>
                  <textarea
                    data-testid="contact-message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>

                <p className="caption text-center">
                  * Required fields. We respect your privacy and will never share your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
